import { QuestionMarkCircleIcon } from "@heroicons/react/24/solid";
import { createContext, ReactNode, useContext, useMemo } from "react";
import useLocalStorage from "util/useLocaleStorage";
import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import Button from "ui/atoms/Button/Button";
import Image from "next/image";
import Divider from "ui/atoms/Divider";
import classNames from "classnames";
import TabContainer from "ui/atoms/Container/TabContainer";

export interface HelpContextState {
  playback: boolean;
  share: boolean;
  melodyChords: boolean;
  range: boolean;
  introModal: boolean;
}

export const HelpContext = createContext<
  readonly [HelpContextState | null, (state: Partial<HelpContextState>) => void]
>([null, () => {}]);

export function HelpContextProvider({ children }: { children: ReactNode }) {
  const [helpState, setHelpState, fetched] = useLocalStorage<HelpContextState>(
    "helpState",
    {
      playback: false,
      melodyChords: false,
      range: false,
      share: false,
      introModal: false,
    }
  );

  const value = useMemo(
    () =>
      [
        fetched ? helpState : null,
        (nextState: Partial<HelpContextState>) => {
          setHelpState((prev) => ({ ...prev, ...nextState }));
        },
      ] as const,
    [fetched, helpState, setHelpState]
  );

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

interface HelpDialogueConfigurableProps {
  marginClassName?: string;
}

interface HelpDialogueProps extends HelpDialogueConfigurableProps {
  children: React.ReactNode;
  helpKey: keyof HelpContextState;
}

function HelpDialogue({
  children,
  helpKey,
  marginClassName,
}: HelpDialogueProps) {
  const [showHelp, setShowHelp] = useContext(HelpContext);

  const open = showHelp && showHelp[helpKey];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={classNames(marginClassName ?? "mb-4")}
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 0 }}
        >
          <TabContainer
            theme="yellow"
            title={"Learning Time"}
            mobileDivider
            subtitle={
              <span>
                Click the{" "}
                <QuestionMarkCircleIcon className={"w-4 inline-block"} /> button
                to toggle this dialog.
              </span>
            }
          >
            <div
              className={"flex items-center px-1 text-base leading-tight mb-1"}
            >
              <div>{children}</div>
            </div>
          </TabContainer>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export const Playback = (props: HelpDialogueConfigurableProps) => (
  <HelpDialogue helpKey={"playback"} {...props}>
    <p className={"mb-2"}>
      This table displays the current notes and chords that you are practicing.
      Each cell will highlight when the note is actively being played.
    </p>

    <p className={"font-bold mb-2"}>Here is what the table means:</p>

    <ul className={"list-disc pl-6"}>
      <li className={"mb-1"}>
        <strong className={"font-bold"}>Scale degrees:</strong>. This row
        displays the degree of the scale for each column. 1 being the root and 8
        being the octave with the inbetween numbers being different intervals
        within the scale.
      </li>

      <li className={"mb-1"}>
        <strong className={"font-bold"}>Melody:</strong> This row represents the
        melody or scale that you are practicing. It will display the actual note
        values of the current transposed scale. Each cell will highlight when it
        is being played back.
      </li>

      <li>
        <strong className={"font-bold"}>Chords:</strong> This row represents the
        chords that are being played back. Each cell will highlight when the
        chord is being played back. Each chord degree plays the correct chord
        type for the scale degree. For example, in C Major the 1st degree will
        play a C Major chord, the 2nd degree will play a D minor chord, etc.
      </li>
    </ul>
  </HelpDialogue>
);

export const Range = (props: HelpDialogueConfigurableProps) => (
  <HelpDialogue helpKey={"range"} {...props}>
    <p className={"mb-2"}>
      Here you can select the range of notes that you want to practice. The
      melody you choose will be transposed up and down within this range. The
      vocal register guides below are just reference points. Everyone is unique
      so take them with a grain of salt.
    </p>

    <p className={"font-bold mb-2"}>How to use this:</p>

    <ul className={"list-disc pl-6 mb-2"}>
      <li className={"mb-1"}>
        Click the note name on the key to hear any of the notes below.
      </li>

      <li className={"mb-1"}>
        Find the lowest note you want to practice and then click the dot on the
        key to select it.
      </li>

      <li className={"mb-1"}>
        Find the highest note you want to practice by playing the notes to what
        sounds right and then click the dot to select it.
      </li>

      <li>
        You will see that the selected range is highlighted now meaning you are
        ready to start practicing.
      </li>

      <li>
        To adjust the range simply click another note and repeat the process
        listed above.
      </li>
    </ul>

    <p className={"mb-2 text-yellow-500"}>
      Note that if your selected range is less than 1 octave your melody may not
      have enough room to transpose up and down within it. This is fine if you
      want to focus on a smaller range.
    </p>
  </HelpDialogue>
);

export const MelodyChords = (props: HelpDialogueConfigurableProps) => (
  <HelpDialogue helpKey={"melodyChords"} {...props}>
    {/* Select the note range below that you want to focus on. We will loop and move
    your selected melody around within this range. Click the key to hear the
    note and click the dot to actually make a selection. You can always adjust
    this at anytime later on as well. */}

    <p className={"mb-2"}>
      Here is where you add the melody you want to practice. When we say melody
      we mean just a series of scale degrees. It could be a simple triad
      arpeggio "1 3 5 8" or a full scale "1 2 3 4 5 6 7 8". Currently, only the
      major scale is supported. Since the major scale only has 8 degrees the
      available scale degrees are 1-8.
    </p>

    <p className={"mb-4"}>
      As you type in the interactive editor you will see the melody being drawn
      in C Major. C Major is chosen purely for visualization purposes as this
      melody will be transposed up and down within your vocal range and the
      actual scale will change constantly.
    </p>

    <p className={"font-brik mb-1"}>
      Here is a quick guide to creating a melody:
    </p>

    <div
      className={
        "border border-white rounded p-2 grid md:grid-cols-3 grid-cols-1 md:gap-1 gap-4"
      }
    >
      <div>
        <p className={"font-bold mb-2"}>Available Scale Degrees</p>

        <ul className={"list-disc pl-6 mb-2"}>
          <li className={"mb-1"}>
            <strong>"1"</strong> Root note
          </li>

          <li className={"mb-1"}>
            <strong>"2"</strong> Major 2nd
          </li>

          <li className={"mb-1"}>
            <strong>"3"</strong> Major 3rd
          </li>

          <li className={"mb-1"}>
            <strong>"4"</strong> Perfect 4th
          </li>

          <li className={"mb-1"}>
            <strong>"5"</strong> Perfect 5th
          </li>

          <li className={"mb-1"}>
            <strong>"6"</strong> Major 6th
          </li>

          <li className={"mb-1"}>
            <strong>"7"</strong> Major 7th
          </li>

          <li>
            <strong>"8"</strong> Octave
          </li>
        </ul>
      </div>

      <div>
        <p className={"font-bold mb-2"}>Available Note Lengths</p>

        <p className={"mb-2"}>
          You can change the note length by appending "-" to a scale degree.
        </p>

        <ul className={"list-disc pl-6 mb-2"}>
          <li className={"mb-1"}>
            <strong>"1"</strong> Quarter note
          </li>

          <li className={"mb-1"}>
            <strong>"1-"</strong> Half note
          </li>

          <li className={"mb-1"}>
            <strong>"1--"</strong> Dotted half note
          </li>

          <li>
            <strong>"1---"</strong> Whole note
          </li>
        </ul>
      </div>

      <div>
        <p className={"font-bold mb-2"}>Rules and tips</p>

        <ul className={"list-disc pl-6 mb-2"}>
          <li className={"mb-1"}>
            Spaces are used to separate each beat. Some examples: "1 3- 5 8---",
            "1--- 1", "1 8", etc.
          </li>

          <li className={"mb-1"}>
            There is a max of 16 notes allowed. Beat length does not count. You
            could have 16 whole notes if desired.
          </li>

          <li>
            Click on one of the examples below to get a feel for how it works!
          </li>
        </ul>
      </div>
    </div>
  </HelpDialogue>
);

export const Share = (props: HelpDialogueConfigurableProps) => (
  <HelpDialogue helpKey={"share"} {...props}>
    <p className={"mb-2"}>
      You can share your current practice session with others by clicking the
      button below to copy the URL. This will generate a shortlink that will
      include the scale, vocal range, and bpm you are practicing.
    </p>

    <p className={"mb-2 font-bold"}>Some things to keep in mind:</p>

    <ul className={"list-disc pl-6 mb-2"}>
      <li className={"mb-1"}>
        If you make changes after copying the URL you will need to copy it again
        to share the updated session.
      </li>

      <li className={"mb-1"}>
        Changes made by others will not affect the URL so you can safely save it
        for later.
      </li>

      <li className={"mb-1"}>
        It is a great way to save your practice sessions for later or share with
        students!
      </li>

      <li>
        <strong className={"font-bold"}>For teachers,</strong> we recommend you
        create a doc with your students and paste relevant links in there so the
        student can practice on their own until the next lesson.
      </li>
    </ul>
  </HelpDialogue>
);

export function IntroModal() {
  const [helpContext, setHelpContext] = useContext(HelpContext);
  const isOpen = helpContext?.introModal ?? false;
  const onClose = () => {
    setHelpContext({ introModal: false });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog
          static
          open={isOpen}
          onClose={onClose}
          className="relative z-50"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50"
          />

          <div className="fixed inset-0 flex w-screen overflow-y-auto items-start justify-center md:pt-4 pt-2 md:px-4 px-2">
            <DialogPanel
              as={motion.div}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="max-w-2xl bg-black border-2 border-white rounded text-white pt-8 mb-16"
            >
              <DialogTitle
                className={
                  "text-4xl leading-none font-brik text-center mb-4 px-4"
                }
              >
                Welcome to Vocal Driller!
              </DialogTitle>

              <Description className={"mb-8 md:px-4 px-2"}>
                {/*
                 *
                 * What does it do
                 *
                 */}

                <h3 className={"font-bold"}>What exactly does this tool do?</h3>

                <p className={"text-xs"}>
                  Enter a scale and enter a vocal range. Press play and it will
                  loop that scale endlessly. At the end of each loop it will
                  transpose it up or down within the vocal range you selected.
                  Instead of juggling playing an instrument and singing at the
                  same time, or using generic accompanying tracks, now you can
                  focus only on your singing and getting better.
                </p>

                <Divider className={"my-4"} />

                {/*
                 *
                 * Scales
                 *
                 */}

                <h3 className={"font-bold"}>
                  Creating Scales and Melodies to Practice
                </h3>

                <p className={"text-xs mb-2"}>
                  The main idea is you enter a scale pattern you want to
                  practice like “1 3 5 8”. These numbers represent the degree of
                  a major scale. 1 being the root, 3 being the major 3rd, 5
                  being the 5th, 8 being the octave, etc. As of now only the
                  degrees 1-8 are supported. A space is used between each number
                  to separate each beat.
                </p>

                <p className={"text-xs mb-2"}>
                  Each number by default is a quarter note. You can use up to 16
                  different notes. Here is what the scale “1 3 5 8” looks like
                  in the editor:
                </p>

                <Image
                  src={`/images/vocal-driller/help/melody-chords-1.png`}
                  width={650}
                  height={175}
                  className="w-full mb-5"
                  alt=""
                />

                <p className={"text-xs mb-2"}>
                  You can created more complicated rhythms within your scale by
                  appending “-” to a scale degree.
                </p>

                <ul className={"list-disc pl-6 text-xs mb-2"}>
                  <li>A single dash, “1-”, is a half note.</li>
                  <li>A double dash, “1--”, is a dotted half note.</li>
                  <li>A triple dash, “1---”, is a whole note.</li>
                </ul>

                <p className={"text-xs mb-2"}>
                  Here is a slightly more complicated chromatic scale using
                  different note lengths:
                </p>

                <Image
                  src={`/images/vocal-driller/help/melody-chords-2.png`}
                  width={650}
                  height={175}
                  className="w-full mb-5"
                  alt=""
                />

                <p className={"text-xs"}>
                  The editor provides realtime feedback and a ton of examples so
                  don't worry too much!
                </p>

                <Divider className={"my-4"} />

                {/*
                 *
                 * Vocal Range
                 *
                 */}

                <h3 className={"font-bold"}>
                  Choosing a Vocal Range to Practice
                </h3>

                <p className={"text-xs mb-2"}>
                  After picking a scale the vocal range you want to work on also
                  needs to be chosen. There are markers for common vocal ranges,
                  but they're just a guide as everyone is unique. When you press
                  play, the scale you picked will be looped and transposed after
                  each loop up and down within the range you select.
                </p>

                <p className={"text-xs mb-2"}>
                  Remember, you can select smaller parts of your overall range
                  to practice. For example, you can practice a scale in the
                  middle of your range to focus on break. Select your head voice
                  or chest voice. The options are endless! Change the range
                  often as you warm up and cool down.
                </p>

                <Image
                  src={`/images/vocal-driller/help/vocal-range-1.png`}
                  width={650}
                  height={175}
                  className="w-full"
                  alt=""
                />

                <Divider className={"my-4"} />

                {/*
                 *
                 * BPM
                 *
                 */}

                <h3 className={"font-bold"}>Choosing a tempo</h3>

                <div className={"grid grid-cols-[1fr,100px] gap-2"}>
                  <div>
                    <p className={"text-xs mb-2"}>
                      You can adjust the tempo of the scale you are practicing
                      dynamically. Simply click the BPM button in the toolbar
                      and either tap tempo, manually type in the value, or press
                      the +/- buttons to adjust the tempo.
                    </p>

                    <p className={"text-xs"}>
                      You can control the metronome volume using the slider at
                      the bottom of the dropdown.
                    </p>
                  </div>

                  <Image
                    src={`/images/vocal-driller/help/bpm-1.png`}
                    width={50}
                    height={300}
                    className="w-full"
                    alt=""
                  />
                </div>

                <Divider className={"my-4"} />

                {/*
                 *
                 * Holding a scale
                 *
                 */}

                <h3 className={"font-bold"}>Holding a scale</h3>

                <div className={"grid grid-cols-[1fr,100px] gap-2"}>
                  <div>
                    <p className={"text-xs"}>
                      You can toggle on the 'Hold' button to stop the scale from
                      transposing up and down. This is useful if you are
                      struggling on a specific scale and want to focus on it
                      until you are comfortable with it.
                    </p>
                  </div>

                  <Image
                    src={`/images/vocal-driller/help/hold-1.png`}
                    width={50}
                    height={300}
                    className="w-full"
                    alt=""
                  />
                </div>
              </Description>

              <div
                className={
                  "flex items-center justify-between sticky bottom-0 bg-black/90 shadow-2xl p-2 border-t border-white"
                }
              >
                <div className={"mr-2 leading-none"}>
                  Click the{" "}
                  <QuestionMarkCircleIcon className={"w-4 inline-block"} />{" "}
                  button at anytime
                  <br />
                  to see this dialog after closing
                </div>

                <Button theme={"green"} size="large" onClick={onClose}>
                  Start!
                </Button>
              </div>
            </DialogPanel>
          </div>
        </Dialog>
      )}
    </AnimatePresence>
  );
}
